import { Form, Modal, notification } from "antd";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IMunicipio, IPlanoGovernoEixo, IPlanoGovernoEixoCreate, IPlanoGovernoEixoForm, IPlanoGovernoEixoMeta, IPlanoGovernoEixoUpdate } from "../../interfaces";
import PlanoGovernoService from "../../../../services/http/services/PlanoGovernoService";
import { useNavigate, useParams } from "react-router-dom";
import { EmitError } from "../../../../utils/EmitError";
import toast from "../../../../utils/toast";

export default function useEixo() {

    const [isLoadingMunicipio, setIsLoadingMunicipio] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingForm, setIsLoadingForm] = useState<boolean>(false);
    const navigate = useNavigate();
    const [eixo, setEixo] = useState<IPlanoGovernoEixo>();
    const [metas, setMetas] = useState<IPlanoGovernoEixoMeta[]>([]);
    const [metaUuidSelected, setMetaUuidSelected] = useState<string>();
    const [formEixo] = Form.useForm();
    const [formMeta] = Form.useForm();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const refContagemDoFiltro = useRef<Array<any>>([]);
    const refFormMeta = useRef<any>(null);
    const refFormEixo = useRef<any>(null);
    const controller = new AbortController();
    const { uuid } = useParams();
    const [citySelected, setCitySelected] = useState<string>();
    const [municipios, setMunicipios] = useState<IMunicipio[]>();


    const handleChangeModal = useCallback(() => {
        if (eixo) {
            getEixoByUuid(eixo?.uuid);
        }
        return setIsModalOpen((prevState) => !prevState);
    }, [eixo]);

    const handleUpdateMeta = useCallback((uuid: string) => {
        setMetaUuidSelected(uuid);
        handleChangeModal();
    }, []);

    const handleCreateMeta = useCallback(() => {
        setMetaUuidSelected(undefined);
        handleChangeModal();
    }, []);

    const handleReturn = useCallback(() => {
        return navigate('/plano-de-governo');
    }, []);

    const getNomeMunicipio = useCallback(() => {

        if (!municipios) {
            return '';
        }
        const findMunicipio = municipios?.find((municipio) => municipio.idIbge === Number(citySelected));

        if (!findMunicipio) {
            return '';
        }
        formEixo.setFieldValue('idIbge', findMunicipio.nome + ' - ' + findMunicipio.estado);

    }, [municipios, citySelected]);


    const getEixoByUuid = useCallback((uuid: string) => {
        const buscaDeDados = async () => {
            try {
                setIsLoading(true);
                let eixo = await PlanoGovernoService.getEixoByUuid(uuid, controller.signal);

                eixo.metas.sort((a: any, b: any) => {
                    return (
                        moment(b.created_at).unix() -
                        moment(a.created_at).unix()
                    );
                });

                formEixo.setFieldsValue({
                    ...eixo,
                });
                setEixo(eixo);
                setMetas(eixo.metas);
                setIsLoading(false);
            } catch (error) {
                // EmitError(error, true, "useEffect");
                setIsLoading(false);
            }
        };
        return buscaDeDados();
    }, []);

    const onClickCancelEixo = useCallback(() => {
        formEixo.setFieldsValue({
            ...eixo,
        });
    }, [eixo]);

    const onClickSalvarEixo = useCallback((): void => {

        refFormEixo.current
            .validateFields()
            .then((values: IPlanoGovernoEixoForm) => {
                values.idIbge = Number(citySelected);

                if (values.uuid) {
                    values.objetivo = values.objetivo ?? "";
                    handleUpdateEixo(values.uuid, values)
                    return;
                }

                handleCreateEixo(values);
            })
            .catch((errorInfo: any) => {
                notification.error({
                    message: "Erro",
                    description: "Preencha os campos obrigatorios!",
                });
            });
    }, [eixo, citySelected]);

    const onClickExcluirMeta = useCallback((uuid: string) => {
        try {
            //Confirmação de exclusão
            Modal.confirm({
                title: "Excluir meta",
                content:
                    "Deseja realmente excluir a meta? (Essa ação não pode ser desfeita)",
                okText: "Sim",
                cancelText: "Não",
                onOk() {

                    setIsLoading(true);
                    PlanoGovernoService.deleteMetaById(uuid).then((result) => {
                        toast({
                            type: 'success',
                            text: 'Meta excluída com sucesso!',
                            duration: 5000,
                        });
                        setIsLoading(false);
                        getEixoByUuid(eixo?.uuid ?? uuid);

                    }).catch(() => {
                        setIsLoading(false);
                    });
                },
                onCancel() { },
            });
        } catch (error) {
            EmitError(error, true, "useEffect");
            setIsLoading(false);
        }
    }, [eixo]);

    const handleUpdateEixo = useCallback(async (uuid: string, eixo: IPlanoGovernoEixoUpdate) => {
        try {

            setIsLoadingForm(true);
            const updatedEixo = await PlanoGovernoService.updateEixo(uuid, eixo);

            if (updatedEixo) {

                toast({
                    type: 'success',
                    text: 'Eixo atualizado com sucesso!',
                    duration: 5000,
                });
                setEixo(updatedEixo);
            }
            setIsLoadingForm(false);
        } catch (err: any) {

            EmitError(err, true);
            setIsLoadingForm(false);
        }
    }, [eixo]);


    const handleCreateEixo = useCallback(async (eixo: IPlanoGovernoEixoCreate) => {
        try {
            setIsLoadingForm(true);
            const createdEixo = await PlanoGovernoService.createEixo(eixo);

            if (createdEixo) {

                toast({
                    type: 'success',
                    text: 'Eixo salvo com sucesso!',
                    duration: 5000,
                });
                setEixo(createdEixo);

                formEixo.setFieldsValue({
                    ...createdEixo,
                });
            }
            setIsLoadingForm(false);
        } catch (err: any) {

            EmitError(err, true);
            setIsLoadingForm(false);
        }
    }, [eixo]);

    const getMunicipios = useCallback(() => {
        const buscaDeDados = async () => {
            try {
                setIsLoadingMunicipio(true);
                let municipio = await PlanoGovernoService.getMunicipios(controller.signal);

                municipio.sort((a: any, b: any) => a.nome.localeCompare(b.nome));

                setMunicipios(municipio);
                setIsLoadingMunicipio(false);
            } catch (error) {
                EmitError(error, true, "useEffect");
                setIsLoadingMunicipio(false);
            }
        };
        return buscaDeDados();
    }, []);

    useMemo(() => {
        if (municipios) {
            getNomeMunicipio();
        }
    }, [citySelected, municipios]);

    useEffect((): void => {
        if (uuid) {
            getEixoByUuid(uuid);
        }
    }, [uuid]);

    useEffect(() => {

        let city = localStorage.getItem("@GovfacilGestor:citySelected");

        if (city) {
            setCitySelected(city);
            getMunicipios();
        }
    }, []);


    return {
        metas,
        isLoading,
        handleReturn,
        refContagemDoFiltro,
        handleChangeModal,
        handleCreateMeta,
        isModalOpen,
        handleUpdateMeta,
        formEixo,
        refFormEixo,
        onClickSalvarEixo,
        eixo,
        onClickCancelEixo,
        isLoadingForm,
        metaUuidSelected,
        onClickExcluirMeta,
        isLoadingMunicipio,
        uuid,
    };
}
